import React, { FC, useState } from 'react';

import { Button, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { publishAliceAgentApp, updateAliceAgentAppIntegrations } from 'client/api/services/aliceAgentApps';
import editStyles from 'client/blocks/AliceAgentAppPage/AliceAgentAppEditPage.module.scss';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { Field } from 'client/blocks/form/Field/Field';
import { useAppForm } from 'client/blocks/form/formHook';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import { getErrorMessage } from 'common/utils/errorsHandler';
import * as agentFormKeyset from 'i18n/AliceAgentAppForm';
import * as keyset from 'i18n/AliceAgentAppIntegrations';
import * as commonKeyset from 'i18n/Common';

import styles from './AliceAgentAppIntegrationsPage.module.scss';

const t = i18n(keyset);
const tForm = i18n(agentFormKeyset);
const tCommon = i18n(commonKeyset);

interface Props {
    app: AliceAgentAppDTO;
    isDraft: boolean;
    onSaved: () => Promise<unknown> | void;
}

const HOST_VALIDATION_KEY = 'integrations.ycp.host';

type TypedError = { key?: string };

/**
 * Backend validation failures arrive as ApiError-shaped objects with `payload.typedErrors`
 * (`.key` like `integrations.ycp.host`). Duck-typed on purpose: importing ApiError pulls the
 * whole `client/api` graph (incl. ESM-only deps) into the Jest bundle.
 */
const extractTypedErrors = (err: unknown): TypedError[] =>
    (err as { payload?: { typedErrors?: TypedError[] } } | null)?.payload?.typedErrors ?? [];

/** Server error key → form field + message. Adding a field/integration = adding an entry here. */
const FIELD_ERROR_MAP: Record<string, { field: string; message: string }> = {
    [HOST_VALIDATION_KEY]: { field: 'ycpHost', message: t('validation-ycp-host') },
};

/** Maps typed errors onto `{ [formField]: message }`; unmapped keys are ignored (fall back to toast). */
const mapServerErrorsToFields = (err: unknown): Record<string, string> => {
    const fields: Record<string, string> = {};
    for (const typed of extractTypedErrors(err)) {
        const mapped = typed.key ? FIELD_ERROR_MAP[typed.key] : undefined;
        if (mapped && !fields[mapped.field]) {
            fields[mapped.field] = mapped.message;
        }
    }
    return fields;
};

/** Client-side mirror of the server's FQDN rules; the server stays authoritative. */
const isValidYcpHost = (host: string): boolean => {
    const trimmed = host.trim();
    if (!trimmed) return true; // empty means "remove integration"
    if (trimmed.length > 253 || trimmed.endsWith('.')) return false;
    const labels = trimmed.split('.');
    if (labels.length < 2) return false;
    return labels.every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label));
};

/** First message for the toast: a mapped field error, else the server message, else the generic one. */
const extractToastMessage = (err: unknown, fields: Record<string, string>): string =>
    Object.values(fields)[0] ||
    // `||` (not `??`): Error.message exists but may be an empty string.
    getErrorMessage(err) ||
    t('toast-save-error');

/** Versioned integrations container tab. Each integration owns a section inside the form. */
export const AliceAgentAppIntegrationsPage: FC<Props> = ({ app, isDraft, onSaved }) => {
    const { add: toasterAdd } = useToaster();
    const source = isDraft ? app.draft : app.published;
    const initialHost = source?.integrations?.ycp?.host ?? '';
    const status = app.draft.status;
    const isProcessing = status === 'mcpProcessing';
    const isFailed = status === 'mcpFailed';
    const [isPublishing, setIsPublishing] = useState(false);

    /**
     * Single assembly point of the PATCH body. The endpoint replaces the WHOLE integrations
     * object, so when a second integration appears its form values must be included here too —
     * extend this builder, not the component tree.
     */
    const buildIntegrationsRequest = (values: { ycpHost: string }) => {
        const host = values.ycpHost.trim();
        // The backend rejects `{ ycp: { host: '' } }` — an empty host is sent as `ycp: null`.
        return { ycp: host ? { host } : null };
    };

    const form = useAppForm({
        defaultValues: { ycpHost: initialHost },
        onSubmit: async ({ value, formApi }) => {
            const request = buildIntegrationsRequest(value);
            try {
                await updateAliceAgentAppIntegrations(app.skillId, request);
                formApi.reset({ ycpHost: value.ycpHost.trim() });
                toasterAdd({ title: t('toast-integrations-saved'), theme: 'success', name: 'INTEGRATIONS' });
                try {
                    await onSaved();
                } catch {
                    // The save itself succeeded — a failed refetch doesn't undo it.
                    toasterAdd({ title: t('toast-load-error'), theme: 'danger', name: 'INTEGRATIONS_REFETCH' });
                }
            } catch (err) {
                const fields = mapServerErrorsToFields(err);
                formApi.setErrorMap({ onSubmit: { form: '', fields } });
                toasterAdd({ title: extractToastMessage(err, fields), theme: 'danger', name: 'INTEGRATIONS' });
            }
        },
    });

    const handlePublish = async () => {
        if (!isDraft || isPublishing || form.state.isDirty || isProcessing || isFailed) return;
        setIsPublishing(true);
        try {
            await publishAliceAgentApp(app.skillId);
            toasterAdd({ title: tForm('toast-published'), theme: 'success', name: 'PUBLISH_ALICE_AGENT_APP' });
            try {
                await onSaved();
            } catch {
                // Published already — a failed refetch doesn't undo it.
                toasterAdd({ title: t('toast-load-error'), theme: 'danger', name: 'INTEGRATIONS_REFETCH' });
            }
        } catch (err) {
            const message = getErrorMessage(err) || tForm('toast-publish-error');
            toasterAdd({ title: message, theme: 'danger', name: 'PUBLISH_ALICE_AGENT_APP' });
        } finally {
            setIsPublishing(false);
        }
    };

    const { Field: FormField, Subscribe } = form;

    if (!isDraft) {
        return (
            <ErrorBoundary withHomepageLink>
                <div className={styles.page}>
                    <h2 className={styles.pageHeader}>{t('side-menu-integrations')}</h2>
                    <div className={styles.section}>
                        {/* Read-only snapshot: no error/footer — a stored invalid host is fixed in the draft. */}
                        <Field
                            contentClassName={styles.field}
                            fieldTitle={t('field-ycp-host')}
                            fieldDescription={t('field-ycp-host-description')}
                        >
                            <TextInput
                                className={styles.fieldInput}
                                value={initialHost}
                                placeholder={t('field-ycp-host-placeholder')}
                                size="l"
                                disabled
                            />
                        </Field>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary withHomepageLink>
            {/* Stretching wrapper (flex + min-height) keeps the footer pinned on a short form;
                the footer lives OUTSIDE <form>, same as the Settings tab. */}
            <div className={styles.form}>
                <form
                    className={editStyles.formContainer}
                    onSubmit={(e) => {
                        e.preventDefault();
                        form.handleSubmit();
                    }}
                >
                    <h2 className={styles.pageHeader}>{t('side-menu-integrations')}</h2>
                    <div className={styles.section}>
                        <FormField
                            name="ycpHost"
                            validators={{
                                // Validate on blur (and on submit), not while typing — same UX as
                                // the rest of the console.
                                onBlur: ({ value }) => (isValidYcpHost(value) ? undefined : t('validation-ycp-host')),
                            }}
                        >
                            {(field) => (
                                <Field
                                    contentClassName={styles.field}
                                    fieldTitle={t('field-ycp-host')}
                                    fieldDescription={t('field-ycp-host-description')}
                                    error={field.state.meta.errors[0] as string | undefined}
                                >
                                    <TextInput
                                        className={styles.fieldInput}
                                        value={field.state.value}
                                        onUpdate={field.handleChange}
                                        onBlur={field.handleBlur}
                                        placeholder={t('field-ycp-host-placeholder')}
                                        size="l"
                                        validationState={field.state.meta.errors[0] ? 'invalid' : undefined}
                                    />
                                </Field>
                            )}
                        </FormField>
                    </div>
                </form>

                <Subscribe selector={(s) => s}>
                    {(s) => (
                        <div className={clx(editStyles.footer, styles.footer)}>
                            <Button
                                view="action"
                                size="l"
                                onClick={() => form.handleSubmit()}
                                disabled={!s.isDirty || !s.canSubmit || s.isSubmitting || isPublishing || isProcessing}
                                loading={s.isSubmitting}
                            >
                                {tCommon('save')}
                            </Button>
                            <Button
                                view="outlined-action"
                                size="l"
                                onClick={handlePublish}
                                disabled={s.isDirty || s.isSubmitting || isPublishing || isProcessing || isFailed}
                                loading={isPublishing}
                            >
                                {tForm('action-publish')}
                            </Button>
                        </div>
                    )}
                </Subscribe>
            </div>
        </ErrorBoundary>
    );
};
